import { TypedJSONObject, JSONObject } from '../types';
import {
  prepareDataForApolloCache,
  QueryInfo,
} from '../prepareDataForApolloCache';
import { token } from '@trello/session-cookie';
import {
  MutationAddFreeTrialArgs,
  MutationAddMembersToOrgArgs,
  MutationApplyBcDiscountArgs,
  MutationUpdateOrganizationArgs,
  MutationCopyBoardToOrgArgs,
  MutationDeleteOrganizationLogoArgs,
  MutationUploadOrganizationImageArgs,
  Organization_Stats_Cards_GroupBy,
  QueryOrganizationMemberCardsArgs,
} from '../generated';
import { confirmPaidAccount } from './paidAccount';
import { clientVersion } from '@trello/config';
import {
  parseNetworkError,
  NetworkError,
  ErrorExtensionsType,
} from '@trello/graphql-error-handling';
import { featureFlagClient } from '@trello/feature-flag-client';

export const organizationNewBillableGuestsResolver = async (
  organization: {
    id: string;
  },
  args: { boardId: string },
  context: object,
  info: QueryInfo,
) => {
  let model = null;

  const apiUrl = `/1/organizations/${organization.id}/newBillableGuests/${args.boardId}`;

  try {
    const response = await fetch(apiUrl, {
      credentials: 'include',
      headers: {
        'X-Trello-Client-Version': clientVersion,
      },
    });

    if (response.ok) {
      model = await response.json();
    } else {
      if (response.status === 404) {
        model = null;
      } else {
        throw new Error(
          `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
        );
      }
    }

    return model
      ? prepareDataForApolloCache(model, info, 'Organization')
      : model;
  } catch (err) {
    console.error(err);
    return model;
  }
};

export const organizationOwnedPluginsResolver = async (
  organization: {
    id: string;
  },
  args: object,
  context: object,
  info: QueryInfo,
): Promise<TypedJSONObject> => {
  let model = null;

  const apiUrl = `/1/organizations/${organization.id}/ownedPlugins`;

  try {
    const response = await fetch(apiUrl, {
      credentials: 'include',
      headers: {
        'X-Trello-Client-Version': clientVersion,
      },
    });

    if (response.ok) {
      model = await response.json();
    } else {
      if ([401, 404, 449].includes(response.status)) {
        model = null;
      } else {
        throw new Error(
          `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
        );
      }
    }

    return model
      ? prepareDataForApolloCache(model, info, 'Organization')
      : model;
  } catch (err) {
    console.error(err);
    return model;
  }
};

export const organizationLabelNamesResolver = async (
  organization: {
    id: string;
  },
  args: object,
  context: object,
  info: QueryInfo,
) => {
  let model = null;

  const apiUrl = `/1/organizations/${organization.id}/stats/labelNames/`;

  try {
    const response = await fetch(apiUrl, {
      credentials: 'include',
      headers: {
        'X-Trello-Client-Version': clientVersion,
      },
    });

    if (response.ok) {
      model = await response.json();
    } else {
      if (response.status === 404) {
        model = null;
      } else {
        throw new Error(
          `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
        );
      }
    }

    const mappedModel =
      Object.entries(model['data'] as JSONObject)
        // Map the labelNames returned from Apollo to LabelFilterOrgLabels
        .map(([name, orgLabel]: [string, JSONObject]) => ({
          name,
          ...orgLabel,
        })) || [];

    return prepareDataForApolloCache(
      {
        incomplete: model.incomplete,
        stats: mappedModel,
      },
      info,
      'Organization_Stats',
    );
  } catch (err) {
    console.error(err);
    return model;
  }
};

const GroupByToIdField: Record<Organization_Stats_Cards_GroupBy, string> = {
  [Organization_Stats_Cards_GroupBy.Label]: 'idLabel',
  [Organization_Stats_Cards_GroupBy.LabelName]: 'labelName',
  [Organization_Stats_Cards_GroupBy.Member]: 'idMember',
};

const assignGroupById = (
  obj: JSONObject,
  id: string,
  groupBy: Organization_Stats_Cards_GroupBy,
) => {
  const idField = GroupByToIdField[groupBy];
  return idField ? { [idField]: id, ...obj } : obj;
};

export const organizationCardStatsResolver = async (
  organizationStats: {
    id: string;
  },
  args: {
    groupBy?: Organization_Stats_Cards_GroupBy;
    idBoards?: string[];
    idMembers?: string[];
    idLabels?: string[];
    dueDateSince?: string;
    dueDateUntil?: string;
  },
  context: object,
  info: QueryInfo,
) => {
  const {
    groupBy,
    idBoards,
    idMembers,
    idLabels,
    dueDateUntil,
    dueDateSince,
  } = args;
  const params = new URLSearchParams({
    ...(groupBy && { groupBy: groupBy }),
    ...(idBoards && idBoards.length > 0 && { idBoards: idBoards.join(',') }),
    ...(idMembers &&
      idMembers.length > 0 && { idMembers: idMembers.join(',') }),
    ...(idLabels && idLabels.length > 0 && { idLabels: idLabels.join(',') }),
    ...(dueDateUntil && { dueDateUntil }),
    ...(dueDateSince && { dueDateSince }),
    includeIds: 'overdue,upcoming',
  });
  const url = `/1/organizations/${
    organizationStats.id
  }/stats/cards?${params.toString()}`;
  const response = await fetch(url, {
    credentials: 'include',
    headers: {
      'X-Trello-Client-Version': clientVersion,
    },
  });
  const json = await response.json();
  const cards = groupBy
    ? Object.entries((json['data'] || {}) as JSONObject).map(
        ([key, value]: [string, JSONObject]) => {
          return assignGroupById(value, key, groupBy);
        },
      )
    : [json['data'] || {}];
  return prepareDataForApolloCache(
    {
      incomplete: json.incomplete,
      stats: cards,
    },
    info,
    'Organization_Stats',
  );
};

export const organizationStatsResolver = (
  organization: {
    id: string;
  },
  args: object,
  context: object,
  info: QueryInfo,
) => {
  return prepareDataForApolloCache(
    {
      id: organization.id,
    },
    info,
    'Organization',
  );
};

export const createOrganization = async (
  obj: object,
  args: {
    displayName: string;
    type: string;
    teamType?: string;
    desc?: string;
    enterprise?: string;
  },
  context: object,
  info: QueryInfo,
) => {
  const response = await fetch('/1/organizations', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': clientVersion,
    },
    body: JSON.stringify({
      ...(args || {}),
      token,
    }),
  });

  if (response.ok) {
    return prepareDataForApolloCache(await response.json(), info);
  }

  throw await parseNetworkError(response);
};

export const updateOrganization = async (
  obj: object,
  args: MutationUpdateOrganizationArgs,
  context: object,
  info: QueryInfo,
) => {
  const response = await fetch(`/1/organizations/${args.orgId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': clientVersion,
    },
    body: JSON.stringify({
      ...(args || {}),
      token,
    }),
  });

  if (response.ok) {
    return prepareDataForApolloCache(await response.json(), info);
  }

  throw await parseNetworkError(response);
};

export async function copyBoardToOrg(
  obj: object,
  args: MutationCopyBoardToOrgArgs,
  context: object,
  info: QueryInfo,
) {
  let response = await fetch(`/1/boards/${args.boardId}`, {
    headers: {
      'X-Trello-Client-Version': clientVersion,
    },
  });
  const copy = await response.json();

  const searchParams = new URLSearchParams();

  searchParams.set('token', token || '');
  searchParams.set('name', copy.name);
  searchParams.set('idOrganization', args.organizationId);
  searchParams.set('idBoardSource', args.boardId);
  searchParams.set('creationMethod', 'assisted');
  searchParams.set('keepFromSource', 'cards');
  searchParams.set('prefs_permissionLevel', 'org');

  response = await fetch(`/1/boards/`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      'X-Trello-Client-Version': clientVersion,
    },
    body: searchParams,
  });

  const board = await response.json();

  return prepareDataForApolloCache(board, info);
}

export const addMembersToOrg = async (
  obj: object,
  {
    orgId,
    users,
    invitationMessage,
    type = 'normal',
  }: MutationAddMembersToOrgArgs,
  context: object,
  info: QueryInfo,
) => {
  try {
    await Promise.all(
      users.map((user) => {
        return fetch(
          `/1/organizations/${orgId}/members${user?.id ? `/${user.id}` : ''}`,
          {
            method: 'PUT',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'X-Trello-Client-Version': clientVersion,
            },
            body: JSON.stringify({
              ...(user?.id
                ? { acceptUnconfirmed: true }
                : { email: user?.email }),
              type,
              invitationMessage,
              token,
            }),
          },
        );
      }),
    );

    return prepareDataForApolloCache({ success: true }, info);
  } catch (err) {
    return prepareDataForApolloCache(
      {
        success: false,
        error: err.message,
      },
      info,
    );
  }
};

export async function uploadOrganizationImage(
  obj: object,
  { orgId, file }: MutationUploadOrganizationImageArgs,
  context: object,
  info: QueryInfo,
) {
  const formData = new FormData();
  formData.set('file', file);
  formData.set('token', token || '');

  // We need to use XHR in order to track upload progress
  const request = new Promise<JSONObject>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.withCredentials = true;

    xhr.open('POST', `/1/organizations/${orgId}/logo`);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const response = JSON.parse(xhr.response);
        resolve(response);
      } else {
        reject(xhr.response);
      }
    };
    xhr.onerror = () => {
      reject(xhr.response);
    };

    xhr.send(formData);
  });

  try {
    const organization = await request;
    return prepareDataForApolloCache(organization, info);
  } catch (err) {
    let error: { message: string; error: string };
    try {
      error = JSON.parse(err);
    } catch (e) {
      error = {
        message: err,
        error: 'UNKNOWN_ERROR',
      };
    }

    throw new NetworkError(error?.message, {
      code: (error?.error as ErrorExtensionsType) || 'UNKNOWN_ERROR',
      status: 400,
    });
  }
}

export const deleteOrganizationLogo = async (
  obj: object,
  { orgId }: MutationDeleteOrganizationLogoArgs,
  context: object,
  info: QueryInfo,
) => {
  const response = await fetch(`/1/organizations/${orgId}/logo`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': clientVersion,
    },
    body: JSON.stringify({
      token,
    }),
  });

  if (!response.ok) {
    throw await parseNetworkError(response);
  }

  const organization = await fetch(`/1/organizations/${orgId}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': clientVersion,
    },
  });

  return prepareDataForApolloCache(await organization.json(), info);
};

export const addFreeTrial = async (
  obj: object,
  { orgId, via }: MutationAddFreeTrialArgs,
  context: object,
  info: QueryInfo,
) => {
  await fetch(`/1/organizations/${orgId}/freeTrial`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': clientVersion,
    },
    body: JSON.stringify({
      token,
      ...(via ? { via } : {}),
    }),
  });

  const data = await confirmPaidAccount(
    `/1/organizations/${orgId}?fields=credits&paidAccount=true&paidAccount_fields=products,standing`,
    {
      products: [110],
      freeTrial: true,
    },
  );

  return prepareDataForApolloCache(data, info);
};

export const applyBCDiscount = async (
  obj: object,
  { orgId }: MutationApplyBcDiscountArgs,
  context: object,
  info: QueryInfo,
) => {
  const res = await fetch(`/1/organizations/${orgId}/promotions/redeem`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': clientVersion,
    },
    body: JSON.stringify({
      token,
      promotion: 'bcWinback',
    }),
  });

  if (!res.ok) {
    const error = await res.text();
    throw new Error(error);
  }

  return true;
};

export const organizationCardsResolver = async (
  organization: {
    id: string;
  },
  args: {
    idBoards: string[];
    limit?: number;
    cursor?: string;
    minDue?: string;
    maxDue?: string;
    dueComplete?: boolean;
    orOverdue?: boolean;
    sortBy?: string;
    idLists?: string[];
    labels?: string[];
    idMembers?: string[];
  },
  _context: object,
  info: QueryInfo,
): Promise<TypedJSONObject> => {
  let model = null;

  try {
    const {
      idBoards,
      limit,
      cursor,
      minDue,
      maxDue,
      dueComplete,
      orOverdue,
      sortBy,
      idLists,
      labels,
      idMembers,
    } = args;
    const params = new URLSearchParams({
      idBoards: idBoards.join(','),
      ...(limit && { limit: `${limit}` }),
      ...(cursor && { cursor }),
      ...(minDue && { minDue }),
      ...(maxDue && { maxDue }),
      ...(dueComplete !== null && { dueComplete: `${dueComplete}` }),
      ...(orOverdue === true && { orOverdue: `${orOverdue}` }),
      ...(sortBy && { sortBy }),
      ...(idLists && { idLists: idLists.join(',') }),
      ...(labels && { labels: labels.join(',') }),
      ...(idMembers && { idMembers: idMembers.join(',') }),
    });

    const apiUrl = `/1/organizations/${
      organization.id
    }/cards?${params.toString()}`;

    const response = await fetch(apiUrl, {
      credentials: 'include',
      headers: {
        'X-Trello-Client-Version': clientVersion,
      },
    });

    // Perform additional requests for load testing. This should be removed in the future.
    const extraRequestCount = featureFlagClient.get(
      'remarkable.mbtv-extra-req-load-testing',
      0,
    );
    for (let i = 0; i < extraRequestCount; i++) {
      fetch(apiUrl, {
        credentials: 'include',
        headers: {
          'X-Trello-Client-Version': clientVersion,
          'X-Trello-Client-Extra-Request': '1',
        },
      });
    }

    if (response.ok) {
      model = await response.json();
    } else {
      if ([400, 401, 404, 422, 449].includes(response.status)) {
        model = null;
        throw new Error(await response.text());
      } else {
        throw new Error(
          `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
        );
      }
    }

    return model
      ? prepareDataForApolloCache(model, info, 'Organization')
      : model;
  } catch (err) {
    console.error(err);
    throw new Error(err);
  }
};

// Very specific resolver to use on /members/id/cards
export const organizationMemberCardsResolver = async (
  obj: object,
  args: QueryOrganizationMemberCardsArgs,
  context: object,
  info: QueryInfo,
) => {
  const { id, idMember } = args;

  const apiUrl = `/1/organization/${id}/members/${idMember}/cards`;

  const params = new URLSearchParams();
  params.set(
    'fields',
    'badges,closed,dateLastActivity,due,dueComplete,idAttachmentCover,idList,idBoard,idMembers,idShort,labels,name,url',
  );
  params.set('board', 'true');
  params.set('board_fields', 'name,closed,memberships');
  params.set('list', 'true');
  params.set('attachments', 'true');
  params.set('stickers', 'all');
  params.set('members', 'true');
  params.set(
    'member_fields',
    'activityBlocked,avatarUrl,bio,bioData,confirmed,fullName,idEnterprise,idMemberReferrer,initials,memberType,nonPublic,products,url,username',
  );

  const response = await fetch(`${apiUrl}?${params}`, {
    credentials: 'include',
    headers: {
      'X-Trello-Client-Version': clientVersion,
    },
  });

  if (response.ok) {
    const model = await response.json();
    return prepareDataForApolloCache(model, info);
  } else {
    throw new Error(
      `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
    );
  }
};
